import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import FavoriteIcon from '@icons/FavoriteIcon';
import MailIcon from '@icons/MailIcon';
import ShareIcon from '@icons/ShareIcon';
import { PropertyChip } from '@molecules/PropertyChip';
import { mapSearchPropertyToProps } from '@general/transforms/property.transform';

const mapOptions = {
    pinStyle: 'pin-s+057af0',
    style: 'streets-v11',
};

const Mapbox = ({
    altText,
    classNames,
    displayLegend = false,
    isLoggedIn,
    properties,
    latitude,
    longitude,
    height = '480',
    width = '1200',
    // 1: display entire world
    // 20: zoom in as much as possible
    zoomLevel = 1,
}) => {
    let framing = `${longitude},${latitude},${zoomLevel},0/`;

    const coordinates = properties
        .filter(property => property._geoloc.lat && property._geoloc.lng)
        .map(property => ({ latitude: property._geoloc.lat, longitude: property._geoloc.lng }));

    const markers = coordinates
        .map(coordinate => `${mapOptions.pinStyle}(${coordinate.longitude},${coordinate.latitude})`)
        .join(',');

    if (displayLegend) {
        const sortedLatitudes = coordinates.map(coordinate => coordinate.latitude).sort();
        const sortedLongitudes = coordinates.map(coordinate => coordinate.longitude).sort();

        const lowerLongitude = sortedLongitudes[sortedLongitudes.length - 1] - 4;
        const lowerLatitude = sortedLatitudes[0] - 0.38;
        const upperLongitude = sortedLongitudes[0] + 0.38;
        const upperLatitude = sortedLatitudes[sortedLatitudes.length - 1] + 0.38;

        framing = `[${lowerLongitude},${lowerLatitude},${upperLongitude},${upperLatitude}]/`;
    }

    const mapUrl =
        `https://api.mapbox.com/styles/v1/mapbox/` +
        `${mapOptions.style}/` +
        `static/` +
        `${markers}/` +
        `${framing}` +
        `${width}x${height}@2x` +
        `?access_token=${process.env.MAPBOX_API_KEY}`;

    return (
        <>
            <div className={`${classNames}`} style={{ position: 'relative' }}>
                {displayLegend && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '0px',
                            bottom: '0px',
                            width: '40%',
                            background: '#fff',
                            overflowY: 'auto',
                            marginLeft: '1.5rem',
                            marginTop: '1.5rem',
                            marginBottom: '1.5rem',
                            padding: '1rem',
                        }}
                    >
                        {properties
                            .map(property =>
                                mapSearchPropertyToProps({
                                    hit: property,
                                })
                            )
                            .map(property => (
                                <div
                                    key={property.objectID}
                                    style={{
                                        boxShadow: '0 1px 6px 0 rgb(0 0 0 / 15%)',
                                        marginBottom: '1rem',
                                        padding: '1rem',
                                    }}
                                >
                                    <Link to={property.internalUrl} className="card-link">
                                        <div className="d-flex justify-content-between">
                                            <PropertyChip
                                                activatedAt={property.activatedAt}
                                                expiresAt={property.expiresAt}
                                                activations={property.activations}
                                            />
                                            <div className="d-flex align-items-center">
                                                <div className="mr-1rem" hidden>
                                                    <ShareIcon title={property.title} url={property.url} />
                                                </div>

                                                <div hidden>
                                                    <MailIcon title={property.title} url={property.url} />
                                                </div>

                                                <FavoriteIcon isLoggedIn />
                                            </div>
                                        </div>
                                        <h3
                                            className="display6 bold mt-3 mb-2"
                                            style={{
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {property.title}
                                        </h3>
                                        <div className="mb-2">
                                            <div
                                                style={{
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {property.displayLocation}
                                            </div>
                                        </div>
                                        <div className="text-gray-dark">
                                            <div>{property.purpose}</div>
                                            <div>{property.populationDensity}</div>
                                            <div>{property.typeOfCenter}</div>
                                            <div>{property.size}</div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                    </div>
                )}
                <img className="img-fluid" src={mapUrl} alt={altText} />
            </div>
        </>
    );
};

Mapbox.propTypes = {
    altText: PropTypes.string,
    classNames: PropTypes.string,
    displayLegend: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    latitude: PropTypes.string.isRequired,
    longitude: PropTypes.string.isRequired,
    properties: PropTypes.array.isRequired,
    height: PropTypes.string,
    width: PropTypes.string,
    zoomLevel: PropTypes.number,
};

export default Mapbox;
